import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Uc2LifecycleRailComponent } from '../../components/uc2-lifecycle-rail/uc2-lifecycle-rail.component';
import { PersonNameService } from '#services';
import { EngineCaseStore } from '../engine-case.store';
import { auditPersonOids, labelFor, priorOwnerOid, roleLabel, toRailStages } from '../engine-case.mapper';
import { EngineStepPanelComponent, StepDecision } from '../engine-step-panel/engine-step-panel.component';
import { buildAuditTrail } from '../../mappers/audit-trail.mapper';
import { WorkflowAuditTrailComponent } from '../../components/workflow-audit-trail/workflow-audit-trail.component';

/** An engine case, read and driven through the engine: lifecycle graph, active step, detection data, and audit trail. */
@Component({
  selector: 'app-engine-case-detail',
  imports: [DatePipe, Uc2LifecycleRailComponent, EngineStepPanelComponent, WorkflowAuditTrailComponent],
  providers: [EngineCaseStore],
  templateUrl: './engine-case-detail.component.html',
  styleUrls: [
    '../../workflow-detail.scss',
    './engine-case-detail.component.scss',
  ],
})
export class EngineCaseDetailComponent {
  caseId = input.required<string>();

  readonly store = inject(EngineCaseStore);
  private _names = inject(PersonNameService);
  readonly labelFor = labelFor;
  readonly roleLabel = roleLabel;
  activeTab = signal<'summary' | 'workflow' | 'recommended' | 'hypotheses' | 'comments' | 'investigation'>('workflow');

  railStages = computed(() => toRailStages(this.store.graph()));
  state = computed(() => this.store.composed()?.state ?? null);
  isUc2 = computed(() => this.state()?.caseType === 'well-exception');
  audit = computed(() => this.store.composed()?.audit.entries ?? []);
  auditTrail = computed(() => {
    const composed = this.store.composed();
    if (!composed) {
      return { days: [] };
    }
    const candidateTitles: Record<string, string> = {};
    for (const candidate of composed.dossier?.candidates ?? []) {
      const title = candidate.explanation['title'];
      candidateTitles[candidate.candidateId] = typeof title === 'string' && title ? title : candidate.candidateId;
    }
    const item = this.store.form()?.item;
    const itemRoles = item?.id && item.targetRole ? { [item.id]: item.targetRole } : {};
    return buildAuditTrail({
      entries: composed.audit.entries,
      nodes: composed.graph.nodes,
      candidateTitles,
      itemRoles,
      nameOf: oid => this._names.nameOf(oid),
    });
  });
  ownerName = computed(() => this._names.nameOf(this.state()?.ownerOid));
  private _returnTargetOid = computed(() => {
    const to = this.store.form()?.return?.to;
    return to ? priorOwnerOid(this.audit(), to) : null;
  });
  returnToName = computed(() => this._names.nameOf(this._returnTargetOid()));
  candidates = computed(() => this.store.composed()?.dossier?.candidates ?? []);
  suggestedSignals = computed(
    () => this.store.composed()?.dossier?.signals.filter(signal => signal.attachMode === 'suggested') ?? [],
  );
  isClosed = computed(() => this.state()?.status !== 'open');

  constructor() {
    effect(() => this.store.load(this.caseId()));
    effect(() =>
      this._names.request([
        this.state()?.ownerOid,
        this._returnTargetOid(),
        ...auditPersonOids(this.audit()),
      ]),
    );
  }

  onDecide(decision: StepDecision): void {
    this.store.decide(decision.outcome, decision.values, decision.reason, { claimStartStep: this.isUc2() });
  }

  candidateTitle(explanation: Record<string, unknown>, fallback: string): string {
    const title = explanation['title'];
    return typeof title === 'string' && title ? title : fallback;
  }
}
